import {
  EngagementOptionEnum,
  ExchangeListingActivityEnum,
  LikertScaleEnum,
  type EngagementOptionEnumType,
  type ExchangeListingActivityEnumType,
  type LikertScaleEnumType,
} from './enums'

export const RFQFeedbackQuestions = {
  leaderboardRankInfluence: `The market maker's leaderboard rank had a large influence on my decision.`,
  loanSizeImportance: 'The size of the loan was one of the most important criteria used while assessing proposals.',
  liquidityKpiInfluence: 'Liquidity KPIs played a significant role in my selection.',
  loanPricingSensitivity: 'I was sensitive to the loan pricing in my assessment of the proposals provided.',
  feesInfluence: 'The fees associated with the selected proposal(s) played a significant role in my decision.',
  profitShareInfluence:
    'The profit share associated with the selected proposal(s) played a significant role in my decision.',
  improvedLiquidityKpisAcceptance:
    'I would like to see improved liquidity KPIs in Round 2, even if it means increasing the size of the loan.',
  lessFavorablePricingAcceptance:
    'I am willing to accept less favorable option pricing in Round 2 if it results in improved liquidity KPIs from the proposals I will receive.',
} as const

export const MMRFQLikertScaleLabels: Record<LikertScaleEnumType, string> = {
  [LikertScaleEnum.StronglyDisagree]: 'Strongly Disagree',
  [LikertScaleEnum.Disagree]: 'Disagree',
  [LikertScaleEnum.Neutral]: 'Neutral',
  [LikertScaleEnum.Agree]: 'Agree',
  [LikertScaleEnum.StronglyAgree]: 'Strongly Agree',
} as const

export const EngagementOptionLabels: Record<EngagementOptionEnumType, string> = {
  [EngagementOptionEnum.LoanCallOption]: 'Loan + Call Option',
  [EngagementOptionEnum.RetainerWorkingCapital]: 'Retainer + Working Capital',
} as const

// Slash/casing don't survive humanizeEnum, so activity labels are spelled out.
export const ExchangeListingActivityLabels: Record<ExchangeListingActivityEnumType, string> = {
  [ExchangeListingActivityEnum.AlphaAirdrop]: 'Alpha / Airdrop',
  [ExchangeListingActivityEnum.Booster]: 'Booster',
  [ExchangeListingActivityEnum.Hodl]: 'HODL',
  [ExchangeListingActivityEnum.SpotListing]: 'Spot Listing',
  [ExchangeListingActivityEnum.SpotTrading]: 'Spot Trading',
} as const

export const MarketMakerServicesOptions = [
  'Exchange Introductions',
  'Incubator / Accelerator',
  'Investment',
  'OTC, TWAP Liquidation',
  'Treasury Management',
]

export const MMRFQKpiTooltips = {
  'CEX, DEX, or EVEN Skew': `
    The values in this row represent the skew breakdown of the total of quotes submitted. The first number shows quotes with predominant CEX skew.

    The second number shows quotes with predominant DEX skew.

    The third number shows quotes with 50/50 distribution.
  `,
  '% of Quotes with Fees Charged per Exchange':
    'The percentage of “Retainer + Working Capital” quotes provided that charge a monthly fee per exchange covered.',
  '% of Quotes with Unlimited Exchange Coverage':
    'The percentage of “Retainer + Working Capital” quotes provided that provide coverage across an unlimited number of exchanges for a flat monthly fee.',
  'Avg. Order Book Dominance, Max %':
    'In the event that you represent greater than “XX%” of liquidity inside (+/-) 2.00% from midpoint, you will no longer have the obligation to maintain the indicated liquidity KPIs. Note, total liquidity is calculated across all centralized & decentralized venues the asset is traded on.',
  '% of Options Priced Within >6 Month Post-Inception':
    'On average, what percentage of tranches with a dynamic strike price will be priced after 6 months of the engagement commencing?',
  '% of Options Priced Within 1-6 Month Post-Inception':
    'On average, what percentage of tranches with a dynamic strike price will be priced within 1 - 6 months of the engagement commencing?',
  '% of Options Priced Within <1 Month Post-Inception':
    'On average, what percentage of tranches with a dynamic strike price will be priced within the first 1 month of the engagement commencing?',
  '% of Tranches Dynamic Price':
    'On average, what percentage of tranches within each loan carry a strike price that is “dynamic”? (Note: dynamic strike prices are calculated using a TWAP whereas fixed strike prices are denominated as a % relative to the spot price.)',
  '% of Tranches Fixed Price':
    'On average, what percentage of tranches within each loan carry a strike price that is “fixed”? (Note: fixed strike prices are denominated as a % relative to the spot price whereas dynamic strike prices are calculated using a TWAP.)',
  'Avg. Number of Tranches':
    'The average number of tranches per loan requested by Market Makers in this round of the RFQ.',
  'Avg. Interest Rate (APR, %)':
    'The average interest rate (offered to the project in exchange for the token loan) provided by Market Makers in this round of the RFQ.',
  'Avg. Token Loan Size (% of Total Supply)':
    'The average size of the loan requested by Market Makers in this round of the RFQ, denominated as a % of the total token supply.',
  'Number of European Options':
    'European options restrict the Market Maker and dictate they only have the right to exercise the option at contract expiration, at the agreed upon strike price.',
  'Number of American Options':
    'American options give the Market Maker a high degree of flexibility and offer them the right to exercise the option at any date during their loan contract, at the agreed upon strike price.',
  'Avg. Loan Tenor (Months)': `Loan tenor is the length of time remaining on a loan, or the amount of time until the loan is due. It's also known as the time-to-maturity of a loan.`,
  'Avg. Token Loan Size (USD)': 'The average size of the token loan, in US Dollar terms.',
  'Avg. Option Premium, per Quote':
    'The average premium of the strike prices at which each market maker has the option (but not obligation) to purchase tokens. Note, the average is value is taken per proposal, with each proposal potentially containing multiple strike prices depending on the number of tranches.',
  'Number of Quotes': 'Number of proposals received',
  'Avg. Token Loan Size (%)': `The average size of the token loan, as a % of your Token Supply.`,
  'Avg. Token Loan Size (Qty.)': `The average size of the token loan, as quantity of tokens.`,
  'Avg. Stablecoin Loan Size (USD)': 'The average size of the stablecoin loan, in US Dollar terms.',
  'Avg. Total Loan Size (USD)': 'The average size of the combined token & stablecoin loan, in US Dollar terms.',
  'Avg. Onboarding Fee (USD)':
    'The average fee charged by the market makers to configure strategies and begin coverage for your project, in US Dollar terms.',
  'Avg. Monthly Fee (USD)':
    'The monthly average fee charged by the market makers to maintain coverage for your project, in US Dollar terms. Note, if the market maker charges on a per exchange basis, Forgd assumes coverage on four exchanges when calculating their estimated monthly fee.',
  'Avg. Profit Share (%)':
    'The average percentage of total profit that will be allocated to market makers if they can successfully grow the value of your loaned capital. Note, market makers calculate profit in a variety of ways.',
}
